import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { RootState } from '@/store/store'
import { Application } from '@/types/application'
import { Gig } from '@/types/gig'
import {
    FiMapPin,
    FiClock,
    FiCalendar,
    FiUser,
    FiStar,
    FiArrowLeft,
    FiCheckCircle,
    FiXCircle,
    FiAlertCircle,
    FiMessageSquare,
    FiBriefcase,
    FiDollarSign,
    FiExternalLink,
    FiRefreshCw
} from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import CountdownTimer from '@/components/ui/CountdownTimer'
import Avatar from '@/components/ui/Avatar'
import Modal from '@/components/ui/Modal'
import { formatTimeAgo } from '@/utils/formatters'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { applicationApi } from '@/services/api/applications'
import { gigApi } from '@/services/api/gigs'
import { CurrencyDisplay } from '@/components/currency'
import EscrowStatus from '@/components/ui/EscrowStatus'

type ExtendedApplication = Application

// Type guard to check if gig is populated
const isGigPopulated = (gig: Application['gig']): gig is Gig => {
    return typeof gig === 'object' && gig !== null && '_id' in gig
}

// Helper to get category name whether it's string or object
const getCategoryName = (category: any) => {
    if (!category) return 'Unknown'
    if (typeof category === 'string') {
        return category
    }
    return category.name || category.slug || 'Unknown'
}

const ApplicationDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbarContext()

    const { user } = useSelector((state: RootState) => state.auth)

    const [application, setApplication] = useState<ExtendedApplication | null>(null)
    const [fullGig, setFullGig] = useState<Gig | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [updating, setUpdating] = useState(false)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [showRequestChangesModal, setShowRequestChangesModal] = useState(false)
    const [requestChangesFeedback, setRequestChangesFeedback] = useState('')
    const [showResubmitModal, setShowResubmitModal] = useState(false)
    const [resubmitData, setResubmitData] = useState({ deliveryUrls: '', description: '' })

    // Fetch application details
    useEffect(() => {
        const fetchApplication = async () => {
            if (!id) return

            try {
                setLoading(true)
                const response = await applicationApi.getApplicationById(id)
                // @ts-expect-error - Intentional nested data structure for API response
                const application = response.data.data.application
                setApplication(application)
                setError(null)

                // Fetch full gig details if gig ID is available
                if (application.gig && typeof application.gig === 'object' && application.gig._id) {
                    try {
                        const gigResponse = await gigApi.getGigById(application.gig._id)
                        // @ts-expect-error - Handle nested response
                        if (gigResponse.data.data?.gig) {
                            // @ts-expect-error - Handle nested response
                            setFullGig(gigResponse.data.data.gig)
                        }
                    } catch (gigErr) {
                        console.error('Failed to fetch full gig details:', gigErr)
                        // Continue even if full gig fetch fails, we have partial data
                    }
                }
            } catch (err: any) {
                console.error('Failed to fetch application:', err)
                const errorMessage = err.response?.data?.error?.message || 'Failed to load application details'
                setError(errorMessage)
                setApplication(null)
            } finally {
                setLoading(false)
            }
        }

        fetchApplication()
    }, [id])

    const handleStatusUpdate = async (newStatus: 'accepted' | 'rejected') => {
        if (!application) return

        try {
            setUpdating(true)

            // Call actual API
            const response = await applicationApi.updateApplicationStatus(application._id, newStatus)
            // @ts-expect-error - Intentional nested data structure for API response
            const updatedApplication = response.data.data.application
            setApplication(updatedApplication)

            showSuccess(`Application ${newStatus} successfully`)

        } catch (err: any) {
            console.error('Failed to update application status:', err)
            const errorDetails = err.response?.data?.error?.details
            const errorMessage = (Array.isArray(errorDetails) && errorDetails.length > 0)
                ? errorDetails.join('\n')
                : err.response?.data?.error?.message || 'Failed to update application status'
            showError(errorMessage)
        } finally {
            setUpdating(false)
        }
    }

    const handleRequestChanges = async () => {
        if (!application || !requestChangesFeedback.trim()) {
            showError('Please provide feedback for the changes')
            return
        }

        try {
            setUpdating(true)

            const response = await applicationApi.requestChanges(application._id, requestChangesFeedback)
            // @ts-expect-error - Intentional nested data structure for API response
            const updatedApplication = response.data.data.application
            setApplication(updatedApplication)

            showSuccess('Changes requested successfully. Worker will be notified.')
            setShowRequestChangesModal(false)
            setRequestChangesFeedback('')

        } catch (err: any) {
            console.error('Failed to request changes:', err)
            const errorDetails = err.response?.data?.error?.details
            const errorMessage = (Array.isArray(errorDetails) && errorDetails.length > 0)
                ? errorDetails.join('\n')
                : err.response?.data?.error?.message || 'Failed to request changes'
            showError(errorMessage)
        } finally {
            setUpdating(false)
        }
    }

    const handleResubmitWork = async () => {
        if (!application || !resubmitData.deliveryUrls.trim() || !resubmitData.description.trim()) {
            showError('Please provide delivery URLs and description')
            return
        }

        try {
            setUpdating(true)

            const response = await applicationApi.resubmitWork(application._id, {
                deliveryUrls: resubmitData.deliveryUrls.split('\n').map(url => url.trim()).filter(Boolean),
                description: resubmitData.description,
                attachments: []
            })
            // @ts-expect-error - Intentional nested data structure for API response
            const updatedApplication = response.data.data.application
            setApplication(updatedApplication)

            showSuccess('Work resubmitted successfully. Employer will review your changes.')
            setShowResubmitModal(false)
            setResubmitData({ deliveryUrls: '', description: '' })

        } catch (err: any) {
            console.error('Failed to resubmit work:', err)
            const errorDetails = err.response?.data?.error?.details
            const errorMessage = (Array.isArray(errorDetails) && errorDetails.length > 0)
                ? errorDetails.join('\n')
                : err.response?.data?.error?.message || 'Failed to resubmit work'
            showError(errorMessage)
        } finally {
            setUpdating(false)
        }
    }

    const handleRefreshApplication = async () => {
        if (!id || isRefreshing) return

        try {
            setIsRefreshing(true)
            const response = await applicationApi.getApplicationById(id)
            // @ts-expect-error - Intentional nested data structure for API response
            const updatedApplication = response.data.data.application
            setApplication(updatedApplication)
            setError(null)

            // Also refresh full gig details if available
            if (updatedApplication.gig && typeof updatedApplication.gig === 'object' && updatedApplication.gig._id) {
                try {
                    const gigResponse = await gigApi.getGigById(updatedApplication.gig._id)
                    // @ts-expect-error - Handle nested response
                    if (gigResponse.data.data?.gig) {
                        // @ts-expect-error - Handle nested response
                        setFullGig(gigResponse.data.data.gig)
                    }
                } catch (gigErr) {
                    console.error('Failed to refresh full gig details:', gigErr)
                }
            }

            showSuccess('Application data refreshed successfully')
        } catch (err: any) {
            console.error('Failed to refresh application:', err)
            const errorMessage = err.response?.data?.error?.message || 'Failed to refresh application'
            showError(errorMessage)
        } finally {
            setIsRefreshing(false)
        }
    }

    const getStatusIcon = (status: Application['status']) => {
        switch (status) {
            case 'pending':
                return <FiClock className="w-5 h-5 text-yellow-500" />
            case 'accepted':
                return <FiCheckCircle className="w-5 h-5 text-green-500" />
            case 'rejected':
                return <FiXCircle className="w-5 h-5 text-red-500" />
            case 'completed':
                return <FiStar className="w-5 h-5 text-blue-500" />
            default:
                return <FiAlertCircle className="w-5 h-5 text-gray-500" />
        }
    }

    const getStatusColor = (status: Application['status']) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
            case 'accepted':
                return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
            case 'rejected':
                return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
            case 'completed':
                return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
            default:
                return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
        }
    }

    const isEmployer = user?.role === 'employer'

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    if (error || !application) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
                    <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-red-700 dark:text-red-400 mb-2">
                        {error || 'Application not found'}
                    </h3>
                    <p className="text-red-600 dark:text-red-300 mb-4">
                        The application you're looking for doesn't exist or may have been removed.
                    </p>
                    <Link
                        to="/dashboard/applications"
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Back to Applications
                    </Link>
                </div>
            </div>
        )
    }

    // Check if gig is populated
    if (!isGigPopulated(application.gig)) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
                    <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-red-700 dark:text-red-400 mb-2">
                        Gig information not available
                    </h3>
                    <p className="text-red-600 dark:text-red-300 mb-4">
                        Unable to load gig details for this application.
                    </p>
                    <Link
                        to="/dashboard/applications"
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        Back to Applications
                    </Link>
                </div>
            </div>
        )
    }

    // At this point, we know gig is populated - use full gig if available, otherwise use the partial one
    const gig = fullGig || (application.gig as Gig)

    return (
        <>
            <Helmet>
                <title>Application Details - SideGO</title>
                <meta name="description" content="View application details and manage status" />
            </Helmet>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header with Back and Refresh Buttons */}
                <div className="mb-6 flex items-center justify-between">
                    <button
                        onClick={() => navigate('/dashboard/applications')}
                        className="inline-flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                    >
                        <FiArrowLeft className="w-4 h-4" />
                        Back to Applications
                    </button>
                    <button
                        onClick={handleRefreshApplication}
                        disabled={isRefreshing}
                        className="inline-flex items-center gap-2 px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Refresh application data"
                    >
                        <FiRefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                        {!isRefreshing && <span className="text-sm">Refresh</span>}
                    </button>
                </div>

                {/* Application Header */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-start justify-between mb-6">
                        <div>
                            <Link
                                to={`/gigs/${gig._id}`}
                                className="text-2xl font-bold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                            >
                                {gig.title}
                            </Link>
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-2">
                                <div className="flex items-center gap-1">
                                    <FiBriefcase className="w-4 h-4" />
                                    <span className="capitalize">{getCategoryName(gig.category)}</span>
                                </div>
                                {gig.location?.location && (
                                    <div className="flex items-center gap-1">
                                        <FiMapPin className="w-4 h-4" />
                                        <span>{gig.location.location}</span>
                                    </div>
                                )}
                                {gig.payment?.amount && gig.payment?.currency && (
                                    <div className="flex items-center gap-1">
                                        <FiDollarSign className="w-4 h-4" />
                                        <span>
                                            <CurrencyDisplay
                                                amount={gig.payment.amount}
                                                currency={gig.payment.currency}
                                                compact
                                            />
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            {getStatusIcon(application.status)}
                            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(application.status)}`}>
                                {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                            </span>
                        </div>
                    </div>

                    {/* Application Timeline */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                                <FiCalendar className="w-4 h-4" />
                                <span>Applied {formatTimeAgo(application.appliedAt || application.createdAt)}</span>
                            </div>
                            {application.updatedAt !== (application.appliedAt || application.createdAt) && (
                                <div className="flex items-center gap-2">
                                    <FiClock className="w-4 h-4" />
                                    <span>Last updated {formatTimeAgo(application.updatedAt)}</span>
                                </div>
                            )}
                        </div>

                        {/* Countdown Timer for Accepted Applications */}
                        {application.status === 'accepted' && isGigPopulated(application.gig) && application.gig.duration && (
                            <div className="mt-6">
                                <CountdownTimer
                                    startTime={application.updatedAt}
                                    duration={application.gig.duration.estimated}
                                    endDate={application.gig.duration.endDate}
                                    className="max-w-lg"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Application Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Application Message */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <FiMessageSquare className="w-5 h-5" />
                                Application Message
                            </h3>
                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                    {application.message || 'No message provided.'}
                                </p>
                            </div>
                        </div>

                        {/* Proposed Rate */}
                        {application.proposedRate && gig.payment?.currency && gig.payment?.type && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                    <FiDollarSign className="w-5 h-5" />
                                    Proposed Rate
                                </h3>
                                <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                                    <CurrencyDisplay
                                        amount={application.proposedRate}
                                        currency={gig.payment.currency}
                                        variant="default"
                                    />
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                    {gig.payment.type === 'hourly' ? 'per hour' :
                                        gig.payment.type === 'fixed' ? 'fixed price' : 'per commission'}
                                </p>
                            </div>
                        )}

                        {/* Worker Profile (for employers) */}
                        {isEmployer && typeof application.worker === 'object' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                    <FiUser className="w-5 h-5" />
                                    Applicant Profile
                                </h3>
                                <div className="flex items-start gap-4">
                                    <Avatar
                                        avatarId={application.worker.profile.avatar}
                                        alt={`${application.worker.profile.firstName} ${application.worker.profile.lastName}`}
                                        size="lg"
                                        className="w-16 h-16 bg-primary-100 dark:bg-primary-900"
                                        fallbackIcon={<FiUser className="w-8 h-8 text-primary-600 dark:text-primary-400" />}
                                    />
                                    <div className="flex-1">
                                        <Link
                                            to={`/profile/${application.worker._id}`}
                                            className="text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                                        >
                                            {application.worker.profile.firstName} {application.worker.profile.lastName}
                                        </Link>
                                        <p className="text-gray-600 dark:text-gray-400 mt-1">
                                            {application.worker.profile.bio || 'No bio available.'}
                                        </p>
                                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                                            {application.worker.rating && (
                                                <div className="flex items-center gap-1">
                                                    <FiStar className="w-4 h-4 text-yellow-500" />
                                                    <span>{application.worker.rating.toFixed(1)}</span>
                                                </div>
                                            )}

                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Gig Summary */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Gig Summary
                            </h3>
                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Category:</span>
                                    <span className="text-gray-900 dark:text-white capitalize">{getCategoryName(gig.category)}</span>
                                </div>
                                {gig.location?.location && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Location:</span>
                                        <span className="text-gray-900 dark:text-white">{gig.location.location}</span>
                                    </div>
                                )}
                                {gig.payment?.amount && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Payment:</span>
                                        <span className="text-gray-900 dark:text-white">
                                            <CurrencyDisplay
                                                amount={gig.payment.amount}
                                                currency={gig.payment.currency}
                                                compact
                                            />
                                        </span>
                                    </div>
                                )}
                                {gig.payment?.type && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600 dark:text-gray-400">Type:</span>
                                        <span className="text-gray-900 dark:text-white capitalize">{gig.payment.type}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400">Urgency:</span>
                                    <span className="text-gray-900 dark:text-white capitalize">{gig.urgency || 'Not specified'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Escrow Status Display */}
                        {application.escrow && gig.payment?.currency && (
                            <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                    Escrow Status
                                </h4>
                                <EscrowStatus
                                    escrowId={application.escrow.escrowId}
                                    escrowStatus={application.escrow.escrowStatus}
                                    amountHeld={application.escrow.amountHeld}
                                    currency={gig.payment.currency}
                                    releasedAt={application.escrow.releasedAt?.toString()}
                                    autoApprovalDeadline={application.review?.autoApprovalDeadline?.toString()}
                                />
                            </div>
                        )}
    
                        {/* Actions (for employers) */}
                        {isEmployer && application.status === 'pending' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Actions
                                </h3>
                                <div className="space-y-3">
                                    <button
                                        onClick={() => handleStatusUpdate('accepted')}
                                        disabled={updating}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <FiCheckCircle className="w-4 h-4" />
                                        Accept Application
                                    </button>
                                    <button
                                        onClick={() => handleStatusUpdate('rejected')}
                                        disabled={updating}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <FiXCircle className="w-4 h-4" />
                                        Reject Application
                                    </button>
                                </div>
                            </div>
                        )}

                        {isEmployer && application.status === 'submitted' && (
                            <>
                                {application.workSubmission && (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                            Submitted Work
                                        </h3>
                                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 space-y-4">
                                            {application.workSubmission.deliveryUrls && application.workSubmission.deliveryUrls.length > 0 && (
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Delivery Links:</p>
                                                    <div className="space-y-2">
                                                        {application.workSubmission.deliveryUrls.map((url: string, index: number) => (
                                                            <a
                                                                key={index}
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors break-all"
                                                            >
                                                                <FiExternalLink className="w-4 h-4 flex-shrink-0" />
                                                                {url}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {application.workSubmission.description && (
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description:</p>
                                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3">
                                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                                            {application.workSubmission.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                        Review Work
                                    </h3>
                                    <div className="space-y-3">
                                        <button
                                            onClick={() => handleStatusUpdate('accepted')}
                                            disabled={updating}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FiCheckCircle className="w-4 h-4" />
                                            Approve Work
                                        </button>
                                        <button
                                            onClick={() => setShowRequestChangesModal(true)}
                                            disabled={updating}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FiAlertCircle className="w-4 h-4" />
                                            Request Changes
                                        </button>
                                        <button
                                            onClick={() => handleStatusUpdate('rejected')}
                                            disabled={updating}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <FiXCircle className="w-4 h-4" />
                                            Reject Work
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        {!isEmployer && application.status === 'in_progress' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                    Resubmit Work
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    The employer has requested changes to your work. Please make the requested modifications and resubmit.
                                </p>
                                {application.review?.feedback && (
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                            <strong>Feedback:</strong> {application.review.feedback}
                                        </p>
                                    </div>
                                )}
                                <button
                                    onClick={() => setShowResubmitModal(true)}
                                    disabled={updating}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <FiCheckCircle className="w-4 h-4" />
                                    Resubmit Work
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showRequestChangesModal && (
                <Modal
                    isOpen={showRequestChangesModal}
                    onClose={() => {
                        setShowRequestChangesModal(false)
                        setRequestChangesFeedback('')
                    }}
                    title="Request Changes"
                    size="md"
                >
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                                Feedback for Changes
                            </label>
                            <textarea
                                value={requestChangesFeedback}
                                onChange={(e) => setRequestChangesFeedback(e.target.value)}
                                placeholder="Describe what changes you'd like the worker to make..."
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                rows={5}
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setShowRequestChangesModal(false)
                                    setRequestChangesFeedback('')
                                }}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRequestChanges}
                                disabled={updating || !requestChangesFeedback.trim()}
                                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {updating ? 'Requesting...' : 'Request Changes'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {showResubmitModal && (
                <Modal
                    isOpen={showResubmitModal}
                    onClose={() => {
                        setShowResubmitModal(false)
                        setResubmitData({ deliveryUrls: '', description: '' })
                    }}
                    title="Resubmit Work"
                    size="md"
                >
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                                Delivery URLs (one per line)
                            </label>
                            <textarea
                                value={resubmitData.deliveryUrls}
                                onChange={(e) => setResubmitData({ ...resubmitData, deliveryUrls: e.target.value })}
                                placeholder="https://example.com/project&#10;https://github.com/user/repo"
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                rows={4}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                                Description
                            </label>
                            <textarea
                                value={resubmitData.description}
                                onChange={(e) => setResubmitData({ ...resubmitData, description: e.target.value })}
                                placeholder="Describe your updated work and the changes you made..."
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                rows={4}
                            />
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setShowResubmitModal(false)
                                    setResubmitData({ deliveryUrls: '', description: '' })
                                }}
                                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResubmitWork}
                                disabled={updating || !resubmitData.deliveryUrls.trim() || !resubmitData.description.trim()}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {updating ? 'Resubmitting...' : 'Resubmit Work'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    )
}

export default ApplicationDetailsPage